import type { MouseEvent } from 'react';
import React, { useMemo } from 'react';
import { withTheme } from 'styled-components';
import Modal from '@this/shared/modal/modal';
import { Link } from '@this/shared/ui/navigations/link';
import type OrderItem from '@this/domain/order_item2';
import { Checkbox } from '@this/shared/ui/inputs/checkbox';
import type Trip from '@this/domain/trip/trip2';
import { Button } from '@this/src/components/shared/ui/inputs/button';

const AIRLINE_CANCEL_URLS: Record<string, { name: string; url: string }> = {
  ANA: {
    name: 'ANA',
    url: 'https://aswbe.ana.co.jp/webapps/servicing/booking-search?CONNECTION_KIND=JPN&LANG=ja'
  },
  JAL: {
    name: 'JAL',
    url: 'https://booking.jal.co.jp/jl/dom-pbkg/manage-booking'
  },
  JTA: {
    name: 'JAL',
    url: 'https://booking.jal.co.jp/jl/dom-pbkg/manage-booking'
  },
  RAC: {
    name: 'JAL',
    url: 'https://booking.jal.co.jp/jl/dom-pbkg/manage-booking'
  },
  SFJ: { name: 'スターフライヤー', url: 'https://rsv.starflyer.jp/rsv_p/aln_web/BERetrieve.do' },
  SNA: { name: 'ソラシドエア', url: 'https://www.solaseedair.jp/reservation/cancel/' },
  ADO: {
    name: 'エアドゥ',
    url: 'https://www.airdo.jp/ap/rsv/reservation/search.html?adol=ja&adom=0&adob=0'
  },
  SKY: { name: 'スカイマーク', url: 'https://www.res.skymark.co.jp/reserve2/?m=confirm&language=ja' },
  JJP: {
    name: 'ジェットスタージャパン',
    url: 'https://booking.jetstar.com/mmb/?pid=subnav:mmb#/login?culture=ja-jp'
  },
  MM: { name: 'ピーチ', url: 'https://manage.flypeach.com/jp/manage/retrieve-booking' },
  FDA: {
    name: 'フジドリームエアライン',
    url: 'https://www.fujidreamairlines.com/bkt/app/RES/top/FRIG410_06BL.do?selectButtonType=1'
  }
};

const AMAKUSA_AIRLINE_CODE = 'AMX';
const AMAKUSA_AIRLINE_NAME = '天草エアライン';
const AMAKUSA_KNOWLEDGE_URL = 'https://aitravel.jp/knowledges/000001195';
const GENERAL_CANCEL_KNOWLEDGE_URL = 'https://aitravel.jp/knowledges/000001075';

type CancelLinkItem = {
  type: 'direct' | 'knowledge';
  airlineCode: string;
  name: string;
  url: string;
  segment: string;
};

interface Props {
  show: boolean;
  submitting: boolean;
  trip: Trip;
  orderItem: OrderItem | null;
  service: { name: string; tel: string };
  handleHide: () => void;
  handleSubmit: (e: MouseEvent<HTMLButtonElement>, orderItemId: number) => void;
  theme: { themeClass: string };
}

const ShinkansenCancelGuide: React.FC = () => {
  const cellStyle: React.CSSProperties = {
    border: '1px solid #999',
    padding: '6px 10px',
    verticalAlign: 'top'
  };
  return (
    <>
      <p style={{ fontWeight: 'bold', marginBottom: '10px', fontSize: 16 }}>
        新幹線（EX-ICを除く）のキャンセルについて
      </p>
      <p style={{ fontWeight: 'bold', marginBottom: '6px' }}>▼キャンセル受付期限について</p>
      <table style={{ borderCollapse: 'collapse', marginBottom: '12px', width: '100%' }}>
        <thead>
          <tr>
            <th style={cellStyle}>出発時刻</th>
            <th style={cellStyle}>キャンセル依頼期限</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td style={cellStyle}>～10:59</td>
            <td style={cellStyle}>前営業日※ 18:00まで</td>
          </tr>
          <tr>
            <td style={cellStyle}>11:00～20:00</td>
            <td style={cellStyle}>出発時刻の2時間前まで</td>
          </tr>
          <tr>
            <td style={cellStyle}>20:01～</td>
            <td style={cellStyle}>当日 18:00まで</td>
          </tr>
        </tbody>
      </table>
      <p style={{ fontSize: 13, marginBottom: '6px' }}>
        ※「前営業日」はプレミアムオプションの有無によって異なります
      </p>
      <p style={{ fontSize: 13, marginBottom: '4px' }}>
        ・あり：土日祝を含む前日 ／ なし：直前の平日（土日祝除く）
      </p>
      <p style={{ fontSize: 13, marginBottom: '20px' }}>
        　例）月曜日出発の場合 → あり：日曜日 18:00／なし：金曜日 18:00
      </p>

      <p style={{ fontWeight: 'bold', marginBottom: '6px' }}>▼キャンセル方法</p>
      <p style={{ marginBottom: '20px' }}>
        ⚠️ 紙のチケット受け取り後は、当社でのキャンセル操作ができません。
        お客様ご自身で、駅窓口でのお手続きをお願いいたします。
      </p>

      <p style={{ fontWeight: 'bold', marginBottom: '4px' }}>紙のチケット受け取り前</p>
      <p style={{ marginBottom: '4px' }}>当社オペレーターがキャンセル手続きを行います。</p>
      <p style={{ marginBottom: '20px' }}>キャンセル依頼ボタンよりキャンセル依頼をお申し込みください。</p>

      <p style={{ fontWeight: 'bold', marginBottom: '4px' }}>紙のチケット受け取り後</p>
      <p style={{ marginBottom: '4px' }}>お客様ご自身での対応が必要です。</p>
      <ol style={{ paddingLeft: '20px', marginBottom: '20px' }}>
        <li>
          列車出発時刻までに駅窓口（みどりの窓口）にて
          <span style={{ fontWeight: 'bold' }}>「払戻証明をお願いします」</span>
          とお伝えください
        </li>
        <li>
          <span style={{ fontWeight: 'bold' }}>「払戻申出」</span>
          の印字があるチケットを、旅程番号を記載のうえ弊社宛に郵送してください
        </li>
      </ol>
    </>
  );
};

const CancelItemModal: React.FC<Props> = ({
  show,
  submitting,
  trip,
  orderItem,
  service,
  handleHide,
  handleSubmit,
  children,
  theme
}) => {
  const [agreedToTerms, setAgreedToTerms] = React.useState(false);
  const [requestConfirmed, setRequestConfirmed] = React.useState(false);
  const styles = useMemo(() => getStyles(), []);

  const useShinkansenSameDayRule =
    orderItem?.orderItemCategory === 'shinkansen' && orderItem?.isExIc !== true && theme.themeClass !== 'tabikobo';

  const isCancellable = useShinkansenSameDayRule ? orderItem?.cancelable === true : trip.cancelRequestable;

  const domesticAirCancelInfo = useMemo(() => {
    const domesticAirTransports = orderItem?.transports.filter(t => t.isDomesticAir && t.airlineCode) ?? [];
    if (domesticAirTransports.length === 0) return null;

    const items: CancelLinkItem[] = [];
    const seenAirlineCodes = new Set<string>();
    let hasUnknownAirline = false;

    domesticAirTransports.forEach(transport => {
      const airlineCode = transport.airlineCode as string;
      if (seenAirlineCodes.has(airlineCode)) return;
      seenAirlineCodes.add(airlineCode);

      const segment = `${transport.from.name ?? ''}→${transport.to.name ?? ''}`;

      if (airlineCode === AMAKUSA_AIRLINE_CODE) {
        items.push({
          type: 'knowledge',
          airlineCode,
          name: AMAKUSA_AIRLINE_NAME,
          url: AMAKUSA_KNOWLEDGE_URL,
          segment
        });
        return;
      }

      const airlineInfo = AIRLINE_CANCEL_URLS[airlineCode];
      if (airlineInfo) {
        items.push({
          type: 'direct',
          airlineCode,
          name: airlineInfo.name,
          url: airlineInfo.url,
          segment
        });
      } else {
        hasUnknownAirline = true;
      }
    });

    if (items.length === 0 && !hasUnknownAirline) return null;

    return { items, hasUnknownAirline };
  }, [orderItem]);

  return (
    <Modal show={show} hideModal={handleHide} maxWidth={1200} title="この予約を本当にキャンセル依頼しますか？">
      {trip.status === 5 ||
      (orderItem?.jobType === 'cancel' && orderItem.status && [7, 13].includes(orderItem.status)) ? (
        <p>この予約はキャンセル済みです</p>
      ) : trip.status === 6 ||
        (orderItem?.jobType === 'cancel' && orderItem.status && ![7, 13].includes(orderItem.status)) ? (
        <p>
          この予約はキャンセル対応中です。
          <br />
          対応が完了するまで少々 お待ち下さい。
        </p>
      ) : submitting ? (
        <p>処理中...</p>
      ) : (
        orderItem && (
          <div style={styles.modalContentWrapper} className="cancel-item-modal__modal-content-wrapper">
            <div style={styles.modalContent} className="cancel-item-modal__modal-content">
              <h1 style={styles.sectionTitle}>キャンセル依頼内容</h1>
              <div style={styles.travelerArea}>
                {orderItem.travelerInformations.map(traveler => (
                  <div key={traveler.id} style={styles.travelerItemArea}>
                    <p style={styles.nameLabel}>氏名</p>
                    <div style={styles.travelerItemNameArea}>
                      <div>{traveler.travelerName}</div>
                      {traveler.user?.email && <div>{traveler.user.email}</div>}
                    </div>
                  </div>
                ))}
              </div>
              <div style={styles.orderItemDetailArea}>
                {children}
                {theme.themeClass === 'tabikobo' && trip.foreignAir && (
                  <p style={styles.cancelFee}>
                    {`キャンセル手数料：${trip.cancelFeeText}`}
                    <br />
                    航空券発券後は、キャンセル手数料の他、キャンセル規程に従いキャンセル料が発生します。
                  </p>
                )}
              </div>
              <div style={styles.consentArea}>
                <h1 style={styles.sectionTitle}>以下の内容を必ずご確認ください</h1>
                <Checkbox checked={agreedToTerms} onChange={e => setAgreedToTerms(e.target.checked)}>
                  右の同意事項・注意事項に同意しました。
                </Checkbox>
                <Checkbox checked={requestConfirmed} onChange={e => setRequestConfirmed(e.target.checked)}>
                  キャンセル依頼内容を確認しました。
                </Checkbox>
              </div>
              {useShinkansenSameDayRule && orderItem.cancelDeadlineText && (
                <div style={{ margin: '10px 0' }}>
                  <p>キャンセル受付期限: {orderItem.cancelDeadlineText} まで</p>
                  <p style={{ color: '#c00', marginTop: '8px' }}>
                    ⚠️ 紙のチケット受け取り後は、当社でのキャンセル操作ができません。
                    お客様ご自身で、駅窓口でのお手続きをお願いいたします。
                  </p>
                </div>
              )}
              <div style={styles.buttonArea}>
                <Button color="sub" onClick={handleHide}>
                  閉じる
                </Button>
                <Button
                  color="primary"
                  onClick={e => handleSubmit(e, orderItem.id)}
                  disabled={!agreedToTerms || !requestConfirmed || !isCancellable}
                >
                  決定する
                </Button>
              </div>
              {!isCancellable && useShinkansenSameDayRule && (
                <div style={{ margin: '10px 0' }}>
                  <p>※ キャンセル受付期限を過ぎているため、当社でのキャンセル操作はできません。</p>
                  <p>
                    紙のチケット受け取り済みの場合は、列車出発時刻までに駅窓口（みどりの窓口）にて
                    「払戻証明をお願いします」とお伝えください。
                  </p>
                </div>
              )}
              {!isCancellable && !useShinkansenSameDayRule && (
                <div style={{ margin: '10px 0' }}>
                  <p>※ キャンセル依頼のリクエストは、ご利用前営業日18時まで可能です。</p>
                  {domesticAirCancelInfo ? (
                    <>
                      {domesticAirCancelInfo.items.some(i => i.type === 'direct') && (
                        <p>ご利用当日のキャンセルは、下記の航空会社公式サイトより可能です。</p>
                      )}
                      <ul style={{ listStyle: 'disc', paddingLeft: '20px' }}>
                        {domesticAirCancelInfo.items.map(item => (
                          <li key={item.airlineCode}>
                            {item.type === 'direct' ? (
                              <>
                                {item.name}（{item.segment}）:
                                <br />
                                <Link isExternal href={item.url} target="_blank" rel="noopener noreferrer">
                                  {item.url}
                                </Link>
                              </>
                            ) : (
                              <>
                                {item.name}（{item.segment}）の変更・キャンセル方法は
                                <Link isExternal href={item.url} target="_blank" rel="noopener noreferrer">
                                  こちら
                                </Link>
                                をご確認ください。
                              </>
                            )}
                          </li>
                        ))}
                        {domesticAirCancelInfo.hasUnknownAirline && (
                          <li>
                            その他航空会社の変更・キャンセル方法は
                            <Link
                              isExternal
                              href={GENERAL_CANCEL_KNOWLEDGE_URL}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              こちら
                            </Link>
                            をご確認ください。
                          </li>
                        )}
                      </ul>
                    </>
                  ) : (
                    <p>
                      ご利用当日のキャンセル依頼に関しましては、当画面の
                      <span style={{ fontWeight: 'bold' }}>[ご利用当日のキャンセル依頼］</span>
                      をご確認ください。
                    </p>
                  )}
                </div>
              )}
            </div>
            <div style={styles.cancelConfirmPolicy} className="cancel-item-modal__cancel-confirm-policy">
              {useShinkansenSameDayRule ? (
                <ShinkansenCancelGuide />
              ) : (
                <>
                  <p style={{ fontWeight: 'bold', marginBottom: '10px' }}>[ご利用当日のキャンセル依頼］</p>
                  <ul style={{ listStyle: 'disc', paddingLeft: '20px', marginBottom: '20px' }}>
                    <li>
                      当日のキャンセルは、ご自身でご対応をお願いいたします。
                      {theme.themeClass !== 'tabikobo' && (
                        <>
                          （キャンセル方法は
                          <Link isExternal href="https://aitravel.jp/knowledges/000001075">
                            こちら
                          </Link>
                          をご確認ください）
                        </>
                      )}
                    </li>
                    <li>
                      ご自身でキャンセルした場合は、弊社にてご請求データを更新する必要がございます。キャンセルがお済みになりましたら、その旨チャットよりお知らせください。
                    </li>
                  </ul>
                </>
              )}

              <p style={{ fontWeight: 'bold', marginBottom: '10px' }}>[キャンセル料金について]</p>
              <ul style={{ listStyle: 'disc', paddingLeft: '20px', marginBottom: '20px' }}>
                <li>キャンセルした場合、キャンセル規程に則りキャンセル料金が発生する場合がございます。</li>
                <li>各提携先の規程により、返金できない場合がございます。</li>
                <li>返金不可プラン等、キャンセル料金が100%発生している場合、返金はございません。</li>
                <li>キャンセルご依頼後、キャンセルの取り消しはできかねます。</li>
                <li>弊社事務手数料は、返金対象外でございます。</li>
                <li>旅程全体をキャンセルしたい場合、旅程詳細画面より一括キャンセル依頼をご利用ください。</li>
              </ul>

              <p style={{ marginBottom: '20px' }}>質問、不明点がある場合は下記までお問い合わせください。</p>
              <table>
                <tbody>
                  <tr>
                    <th>ツール</th>
                    <th>詳細</th>
                  </tr>
                  <tr>
                    <td>チャット</td>
                    <td>
                      <Link href={`/trips/${trip.id}`}>チャットへのリンク</Link>
                    </td>
                  </tr>
                  <tr>
                    <td>電話番号</td>
                    <td>{service.tel}</td>
                  </tr>
                  <tr>
                    <td>問い合わせフォーム</td>
                    <td>
                      <Link href="/inquiries/new">お問い合わせフォームへのリンク</Link>
                    </td>
                  </tr>
                </tbody>
              </table>
              <p style={{ marginBottom: '40px' }}>
                ※平日18時以降、土日、祝日にご依頼いただいたキャンセル対応は翌営業日の対応になります。予めご了承くださいますようお願い申し上げます。
              </p>
            </div>
          </div>
        )
      )}
    </Modal>
  );
};

const getStyles = () => ({
  cancelFee: {
    marginBottom: 10
  },
  modalContentWrapper: {
    display: 'flex' as const,
    flexWrap: 'wrap' as const,
    gap: 20
  },
  modalContent: {
    display: 'flex' as const,
    flexDirection: 'column' as const,
    flex: 6,
    minWidth: 0
  },
  cancelConfirmPolicy: {
    padding: 20,
    background: '#eee',
    flex: 4,
    minWidth: 0,
    width: 'auto'
  },
  consentArea: {
    display: 'flex' as const,
    flexDirection: 'column' as const,
    gap: 10,
    margin: '40px 0 20px'
  },
  sectionTitle: {
    fontSize: 18,
    marginBottom: 10
  },
  travelerArea: {
    display: 'flex' as const,
    flexDirection: 'column' as const,
    gap: 10,
    marginBottom: 40
  },
  travelerItemArea: {
    display: 'flex' as const,
    flexDirection: 'column' as const,
    gap: 4
  },
  travelerItemNameArea: {
    display: 'flex' as const,
    flexDirection: 'row' as const,
    gap: 10
  },
  nameLabel: {
    fontSize: 14,
    fontWeight: 'bold' as const,
    lineHeight: 1.6
  },
  orderItemDetailArea: {
    display: 'flex' as const,
    flexDirection: 'column' as const
  },
  buttonArea: {
    display: 'flex' as const,
    justifyContent: 'center' as const,
    gap: 20
  }
});

export default withTheme(CancelItemModal);
